import { AxiosPromise } from 'axios';
import { Args, AxiosConfig } from './BaseService';
import { CrudService } from './CrudService';
import { resources } from '../resources';
import { ApiScopeDto } from '../models/apiClient/ApiScopeDto';
import { ApiClientListDto } from '../models/apiClient/ApiClientListDto';
import { ApiClientDetailDto } from '../models/apiClient/ApiClientDetailDto';
import { ApiClientCreateDto } from '../models/apiClient/ApiClientCreateDto';
import { ApiClientUpdateDto } from '../models/apiClient/ApiClientUpdateDto';
import { ApiClientActiveDto } from '../models/apiClient/ApiClientActiveDto';
import { ApiClientKeyCreateDto } from '../models/apiClient/ApiClientKeyCreateDto';
import { ApiClientKeyDto } from '../models/apiClient/ApiClientKeyDto';
import { ApiClientLogDto } from '../models/apiClient/ApiClientLogDto';
import { ApiClientLogQueryParams } from '../params/ApiClientLogQueryParams';
import { IntegrationVisitSettingsDto } from '../models/apiClient/IntegrationVisitSettingsDto';

export class ApiClientService extends CrudService<ApiClientDetailDto, Args, ApiClientCreateDto | ApiClientUpdateDto> {
  constructor() {
    super({
      get: resources.detailApiClient,
      create: resources.createApiClient,
      update: resources.updateApiClient,
      delete: resources.deleteApiClient,
      idKey: 'apiClientId',
    });
  }

  public getScopes(options?: AxiosConfig): AxiosPromise<ApiScopeDto[]> {
    return this.get<ApiScopeDto[]>(resources.apiClientScopes, undefined, options);
  }

  public list(args?: Args, options?: AxiosConfig): AxiosPromise<ApiClientListDto[]> {
    return this.get<ApiClientListDto[]>(resources.listApiClients, args, options);
  }

  public setActive(apiClientId: string, active: boolean, options?: AxiosConfig): AxiosPromise<void> {
    const body: ApiClientActiveDto = { active };
    return this.put<void>(resources.setActiveApiClient, body, { apiClientId }, options);
  }

  public addKey(apiClientId: string, body: ApiClientKeyCreateDto, options?: AxiosConfig): AxiosPromise<ApiClientKeyDto> {
    return this.post<ApiClientKeyDto>(resources.addApiClientKey, body, { apiClientId }, options);
  }

  /** `isLastKey` only picks the confirmation text: revoking the last key stops the integration. */
  public revokeKey(apiClientId: string, apiClientKeyId: string, isLastKey: boolean, options?: AxiosConfig): Promise<boolean> {
    const resource = isLastKey ? resources.revokeLastApiClientKey : resources.revokeApiClientKey;
    return this.delete<void>(resource, { apiClientId, apiClientKeyId }, options).then((r) => r.status !== 0);
  }

  public getLogs(apiClientId: string, params?: ApiClientLogQueryParams, options?: AxiosConfig): AxiosPromise<ApiClientLogDto[]> {
    return this.get<ApiClientLogDto[]>(resources.apiClientLogs, { apiClientId, ...params }, options);
  }

  public getIntegrationVisitSettings(options?: AxiosConfig): AxiosPromise<IntegrationVisitSettingsDto> {
    return this.get<IntegrationVisitSettingsDto>(resources.getIntegrationVisitSettings, undefined, options);
  }

  /** Replaces all the settings and returns them as saved (types trimmed and upper-cased). */
  public updateIntegrationVisitSettings(body: IntegrationVisitSettingsDto, options?: AxiosConfig): AxiosPromise<IntegrationVisitSettingsDto> {
    return this.put<IntegrationVisitSettingsDto>(resources.updateIntegrationVisitSettings, body, undefined, options);
  }
}
